import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, Play, RefreshCw, CheckCircle2, XCircle, Clock, AlertTriangle } from "lucide-react";

const statusMeta = {
  sent: { c: "text-[#39FF14] border-[#39FF14]/30 bg-[#39FF14]/10", i: CheckCircle2, t: "Enviado" },
  error: { c: "text-[#FF3B30] border-[#FF3B30]/30 bg-[#FF3B30]/10", i: XCircle, t: "Error" },
  skipped: { c: "text-[#FFD60A] border-[#FFD60A]/30 bg-[#FFD60A]/10", i: AlertTriangle, t: "Omitido" },
  not_configured: { c: "text-white/50 border-white/20 bg-white/5", i: Clock, t: "Sin configurar" },
  pending: { c: "text-white/50 border-white/20 bg-white/5", i: Clock, t: "Pendiente" },
};
const typeLabel = { reminder_3d: "Recordatorio (3 días)", expiry_day: "Vence hoy", payment: "Confirmación de pago" };

export default function Notifications() {
  const [config, setConfig] = useState(null);
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => {
    api.get("/notifications?limit=150").then((r) => setItems(r.data));
    api.get("/notifications/config").then((r) => setConfig(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/notifications/run-daily");
      toast.success(`Revisados ${data.processed} socios · ${data.sent} avisos enviados`);
      load();
    } catch { toast.error("No se pudo ejecutar la revisión"); }
    finally { setRunning(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div><p className="overline text-[#00f0ff]">Recordatorios WhatsApp</p><h1 className="font-display text-5xl">AVISOS</h1></div>
        <div className="flex gap-2">
          <button data-testid="notif-refresh" onClick={load} className="flex items-center gap-2 border border-white/20 px-4 py-2.5 text-sm hover:bg-white/5"><RefreshCw className="w-4 h-4" /> Actualizar</button>
          <button data-testid="notif-run-now" onClick={runNow} disabled={running} className="flex items-center gap-2 bg-[#00f0ff] text-black font-bold uppercase text-sm px-5 py-2.5 hover:bg-[#00c2cc] disabled:opacity-50">
            <Play className="w-4 h-4" /> {running ? "Procesando..." : "Ejecutar avisos ahora"}
          </button>
        </div>
      </div>

      <div data-testid="notif-config-banner" className={`mb-6 flex items-start gap-3 border p-4 ${config?.configured ? "border-[#39FF14]/40 bg-[#39FF14]/5" : "border-[#FFD60A]/40 bg-[#FFD60A]/5"}`}>
        <MessageCircle className={`w-5 h-5 mt-0.5 ${config?.configured ? "text-[#39FF14]" : "text-[#FFD60A]"}`} />
        <div className="text-sm">
          {config?.configured ? (
            <>
              <p className="font-bold text-[#39FF14]">WhatsApp conectado</p>
              <p className="text-white/60 font-mono-logs text-xs">Remitente: {config.from}</p>
            </>
          ) : (
            <>
              <p className="font-bold text-[#FFD60A]">WhatsApp no configurado</p>
              <p className="text-white/60">Falta cargar las credenciales de Twilio (Account SID, Auth Token y número de WhatsApp) en el servidor. Los avisos quedan registrados pero no se envían hasta configurar.</p>
            </>
          )}
          <p className="text-white/40 text-xs mt-2">Automático diario a las 09:00 · Recordatorio 3 días antes · Aviso el día del vencimiento · Confirmación al registrar un pago.</p>
        </div>
      </div>

      <div className="bg-[#0a0a0a] border border-white/10 overflow-x-auto">
        <table className="w-full text-sm" data-testid="notifications-table">
          <thead><tr className="text-left overline border-b border-white/10">
            <th className="px-4 py-4">Socio</th><th className="px-4 py-4">Tipo</th><th className="px-4 py-4">Destino</th>
            <th className="px-4 py-4">Mensaje</th><th className="px-4 py-4">Fecha</th><th className="px-4 py-4">Estado</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="6" className="px-4 py-10 text-center text-white/30">Aún no hay avisos registrados.</td></tr>}
            {items.map((n) => {
              const sm = statusMeta[n.status] || statusMeta.pending;
              return (
                <tr key={n.id} className="border-b border-white/5 hover:bg-white/5">
                  <td className="px-4 py-3 font-semibold">{n.member_name}</td>
                  <td className="px-4 py-3 text-white/70">{typeLabel[n.type] || n.type}</td>
                  <td className="px-4 py-3 font-mono-logs text-xs">{n.to || "—"}</td>
                  <td className="px-4 py-3 text-white/50 text-xs max-w-xs truncate" title={n.body}>{n.body}</td>
                  <td className="px-4 py-3 font-mono-logs text-xs">{new Date(n.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3"><span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-bold uppercase border ${sm.c}`}><sm.i className="w-3.5 h-3.5" />{sm.t}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
